/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FileText, Sparkle, DownloadSimple, EnvelopeSimple, CaretDown,
} from "@phosphor-icons/react";

/**
 * Solicitor-only "Generate from template" dialog. Lists every active template,
 * lets the solicitor pick one (and optionally pick which client drives the
 * merge if the case has multiple), runs the merge on the backend, then both
 * uploads the rendered .docx + .pdf to the case Documents tab AND downloads
 * them locally so the user can edit / send straight away.
 */
export default function GenerateFromTemplateButton({ caseId, caseTitle, clients, referrer, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTplId, setSelectedTplId] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [generating, setGenerating] = useState(false);
  // Email side panel state — collapsed by default so the "just generate"
  // flow stays one click. Expanding it switches the primary action to
  // "Generate & Email".
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailToClients, setEmailToClients] = useState(true);
  const [emailToReferrer, setEmailToReferrer] = useState(false);
  const [emailCustom, setEmailCustom] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [attachFormat, setAttachFormat] = useState("pdf");

  // Editable form fields — built from the picked template's merge
  // tags + their auto-resolved values for this case. Solicitor can
  // tweak any field before generating; blanks fall back to the
  // case-derived value silently. Labels are humanised from the slug
  // ({{client_name}} → "Client name") so the user never sees raw tag
  // syntax. The custom_tags label registry lets template authors
  // override a tag's display label per-template if they want.
  const [fields, setFields] = useState([]); // [{tag, label, value, source}]
  const [fieldsLoading, setFieldsLoading] = useState(false);

  const humaniseTag = (tag) => {
    // Convert "client_name" / "property.address.line_1" → "Client name"
    // / "Property address line 1". Defensive against camelCase tags.
    const last = (tag || "").split(".").pop();
    return last
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (c) => c.toUpperCase());
  };

  useEffect(() => {
    if (!open || !selectedTplId) {
      setFields([]);
      return;
    }
    setFieldsLoading(true);
    Promise.all([
      API.get(`/document-templates/${selectedTplId}/preview-tags`, { params: { case_id: caseId } }),
      API.get(`/cases/${caseId}/register-prefill`).catch(() => ({ data: { fields: {} } })),
    ])
      .then(([{ data }, regRes]) => {
        const regFields = regRes?.data?.fields || {};
        // Use the template-author's custom label registry where set,
        // otherwise humanise the slug client-side. Pre-fill the value
        // with whatever the backend resolved from case data so the
        // solicitor mostly just confirms + submits. Flag values that came
        // from the HMLR title register so they can be double-checked.
        const rows = (data.tags || []).map((t) => ({
          tag: t.tag,
          label: t.label || humaniseTag(t.tag),
          value: t.value || "",
          source: t.source,
          kind: t.kind || "text",
          regValue: regFields[t.tag] || "",
        }));
        setFields(rows);
      })
      .catch(() => setFields([]))
      .finally(() => setFieldsLoading(false));
  }, [open, selectedTplId, caseId]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    API.get("/document-templates")
      .then(({ data }) => setTemplates(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load templates"))
      .finally(() => setLoading(false));
  }, [open]);

  // Auto-pick first template + first non-pending client once data loads.
  useEffect(() => {
    if (templates.length === 1 && !selectedTplId) setSelectedTplId(templates[0].id);
  }, [templates, selectedTplId]);
  useEffect(() => {
    if (clients?.length === 1 && !selectedClientId && clients[0].id) {
      setSelectedClientId(clients[0].id);
    }
  }, [clients, selectedClientId]);

  const downloadBlob = async (doc) => {
    if (!doc) return;
    try {
      const { data } = await API.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: doc.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      toast.error(`Could not auto-download ${doc.original_filename}`);
    }
  };

  const handleGenerate = async () => {
    if (!selectedTplId) {
      toast.error("Pick a template first");
      return;
    }
    setGenerating(true);
    try {
      const body = {};
      if (selectedClientId) body.client_id = selectedClientId;
      // Send the editable form values as field_overrides — the backend
      // merges them on top of the auto-resolved context. Empty inputs
      // are skipped so the user can leave a field blank to fall back
      // to the case-derived value.
      const overrides = {};
      for (const f of fields) {
        const v = (f.value || "").trim();
        if (v) overrides[f.tag] = v;
      }
      if (Object.keys(overrides).length) body.field_overrides = overrides;
      if (emailEnabled) {
        const customList = emailCustom.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
        body.email = {
          send: true,
          to_clients: emailToClients,
          to_referrer: emailToReferrer,
          to_custom: customList,
          subject: emailSubject.trim() || null,
          body: emailBody.trim() || null,
          attach_format: attachFormat,
        };
      }
      const { data } = await API.post(
        `/cases/${caseId}/document-templates/${selectedTplId}/generate`,
        body,
      );
      onGenerated?.(data);
      // Email + generation feedback
      if (data.email) {
        if (data.email.ok) {
          toast.success(
            data.email.mock
              ? `Generated + emailed (mock, ${data.email.recipients?.length || 0} recipient${data.email.recipients?.length === 1 ? "" : "s"} — check /tmp/email_outbox)`
              : `Generated + emailed to ${data.email.recipients?.length || 0} recipient${data.email.recipients?.length === 1 ? "" : "s"}`
          );
        } else {
          toast.error(`Email failed: ${data.email.error || "unknown error"}`);
        }
      } else {
        toast.success(
          data.pdf_error
            ? "Word document generated — PDF conversion failed (see Documents tab)"
            : "Word + PDF generated and attached to Documents"
        );
      }
      // Trigger local downloads (Word first, then PDF on a slight delay so
      // browsers don't suppress the second one).
      await downloadBlob(data.docx);
      setTimeout(() => downloadBlob(data.pdf), 600);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const realClients = (clients || []).filter((c) => c.id);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        data-testid="generate-from-template-btn"
        className="h-9 px-3 text-sm border-slate-200 gap-1.5"
      >
        <Sparkle weight="bold" className="w-4 h-4 text-violet-500" /> Generate from template
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="generate-template-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Generate document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-500">Template</label>
              {loading ? (
                <div className="h-9 rounded-md bg-slate-100 animate-pulse" />
              ) : templates.length === 0 ? (
                <p className="text-xs text-slate-400 py-2">
                  No templates yet. Ask an admin to upload one on <span className="font-medium">Templates</span>.
                </p>
              ) : (
                <Select value={selectedTplId} onValueChange={setSelectedTplId}>
                  <SelectTrigger data-testid="generate-template-select"><SelectValue placeholder="Select a template" /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="inline-flex items-center gap-2">
                          <FileText weight="bold" className="w-3.5 h-3.5 text-slate-400" />
                          {t.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {realClients.length > 1 && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500">Merge against client</label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger data-testid="generate-client-select"><SelectValue placeholder="Primary client (default)" /></SelectTrigger>
                  <SelectContent>
                    {realClients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name} · {c.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Editable merge fields — one input per merge tag the
                template uses, pre-filled from the case. The solicitor
                can override any value (e.g. a custom paragraph) before
                generating. Labels are humanised so the user never sees
                `{{client_name}}` — the tag drives the merge silently
                in the background. */}
            {selectedTplId && (
              <div className="border border-slate-200 rounded-lg p-3" data-testid="template-form-fields">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-700">Fill in the fields</p>
                  <p className="text-[10px] text-slate-400">{fields.length} field{fields.length === 1 ? "" : "s"}</p>
                </div>
                {fieldsLoading ? (
                  <p className="text-xs text-slate-400 py-2">Loading template fields…</p>
                ) : fields.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2">This template has no merge fields — it&apos;ll generate as-is.</p>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {fields.map((f, idx) => (
                      <div key={f.tag} className="space-y-1" data-testid={`template-form-row-${f.tag}`}>
                        <label className="text-[11px] font-medium text-slate-600 flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1.5">
                            {f.label}
                            {f.regValue && f.value === f.regValue && (
                              <span title="Auto-filled from the HMLR title register — please verify" className="text-[8px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 px-1 py-px rounded uppercase tracking-wide" data-testid={`template-form-hmlr-${f.tag}`}>HMLR</span>
                            )}
                          </span>
                          {f.source === "unresolved" && (
                            <span className="text-[9px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded uppercase tracking-wide">Manual</span>
                          )}
                        </label>
                        {f.kind === "boolean" ? (
                          // Yes/No segmented toggle — stored as the
                          // string "Yes" / "No" so the docx merge gets
                          // a directly-renderable value.
                          <div className="inline-flex items-center bg-slate-100 rounded-md p-0.5" data-testid={`template-form-input-${f.tag}`}>
                            {["Yes", "No"].map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => {
                                  const next = [...fields];
                                  next[idx] = { ...next[idx], value: opt };
                                  setFields(next);
                                }}
                                className={`px-3 py-1 text-xs font-semibold rounded ${
                                  f.value === opt
                                    ? "bg-white text-slate-900 shadow-sm"
                                    : "text-slate-500 hover:text-slate-700"
                                }`}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        ) : f.kind === "multiline" ? (
                          <Textarea
                            value={f.value}
                            onChange={(e) => {
                              const next = [...fields];
                              next[idx] = { ...next[idx], value: e.target.value };
                              setFields(next);
                            }}
                            rows={5}
                            placeholder={f.source === "unresolved" ? "Enter details… (no character limit)" : "(auto from case data)"}
                            className="text-xs min-h-[96px]"
                            data-testid={`template-form-input-${f.tag}`}
                          />
                        ) : f.kind === "date" ? (
                          <Input
                            type="date"
                            value={f.value}
                            onChange={(e) => {
                              const next = [...fields];
                              next[idx] = { ...next[idx], value: e.target.value };
                              setFields(next);
                            }}
                            className="h-8 text-xs"
                            data-testid={`template-form-input-${f.tag}`}
                          />
                        ) : f.kind === "currency" ? (
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none">£</span>
                            <Input
                              type="number"
                              inputMode="decimal"
                              step="0.01"
                              value={f.value}
                              onChange={(e) => {
                                const next = [...fields];
                                next[idx] = { ...next[idx], value: e.target.value };
                                setFields(next);
                              }}
                              placeholder="0.00"
                              className="h-8 text-xs pl-5"
                              data-testid={`template-form-input-${f.tag}`}
                            />
                          </div>
                        ) : f.kind === "number" ? (
                          <Input
                            type="number"
                            inputMode="numeric"
                            value={f.value}
                            onChange={(e) => {
                              const next = [...fields];
                              next[idx] = { ...next[idx], value: e.target.value };
                              setFields(next);
                            }}
                            placeholder="0"
                            className="h-8 text-xs"
                            data-testid={`template-form-input-${f.tag}`}
                          />
                        ) : (
                          <Input
                            value={f.value}
                            onChange={(e) => {
                              const next = [...fields];
                              next[idx] = { ...next[idx], value: e.target.value };
                              setFields(next);
                            }}
                            placeholder={f.source === "unresolved" ? "Enter value…" : "(auto from case data)"}
                            className="h-8 text-xs"
                            data-testid={`template-form-input-${f.tag}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Email opt-in — collapsed by default; expanding switches the
                primary action to "Generate & Email" so it's clear we're now
                doing two things instead of one. */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setEmailEnabled((v) => !v)}
                data-testid="generate-email-toggle"
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition-colors ${emailEnabled ? "bg-violet-50" : "bg-slate-50 hover:bg-slate-100"}`}
              >
                <span className="flex items-center gap-2 text-xs font-medium text-slate-800">
                  <EnvelopeSimple weight="bold" className={`w-4 h-4 ${emailEnabled ? "text-violet-600" : "text-slate-400"}`} />
                  Email this document
                </span>
                <CaretDown weight="bold" className={`w-3 h-3 text-slate-400 transition-transform ${emailEnabled ? "rotate-180" : ""}`} />
              </button>
              {emailEnabled && (
                <div className="p-3 space-y-3 border-t border-slate-200 bg-white" data-testid="generate-email-panel">
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-medium text-slate-500">Recipients</p>
                    {realClients.length > 0 && (
                      <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                        <Checkbox checked={emailToClients} onCheckedChange={setEmailToClients} data-testid="email-to-clients" />
                        Clients on this case ({realClients.length})
                      </label>
                    )}
                    {referrer?.email && (
                      <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                        <Checkbox checked={emailToReferrer} onCheckedChange={setEmailToReferrer} data-testid="email-to-referrer" />
                        Referrer ({referrer.name || referrer.email})
                      </label>
                    )}
                    <Input
                      value={emailCustom}
                      onChange={(e) => setEmailCustom(e.target.value)}
                      placeholder="Other emails (comma-separated)"
                      className="h-8 text-xs"
                      data-testid="email-to-custom"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-medium text-slate-500">Subject (optional)</p>
                    <Input
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Defaults to “{Template name} — {Case title}”"
                      className="h-8 text-xs"
                      data-testid="email-subject"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-medium text-slate-500">Message (optional)</p>
                    <Textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="Defaults to a friendly cover note. Leave blank to use it."
                      rows={3}
                      className="text-xs"
                      data-testid="email-body"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-medium text-slate-500">Attach</p>
                    <Select value={attachFormat} onValueChange={setAttachFormat}>
                      <SelectTrigger className="h-8 text-xs" data-testid="email-attach-format"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pdf">PDF only</SelectItem>
                        <SelectItem value="docx">Word only</SelectItem>
                        <SelectItem value="both">Both PDF + Word</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            {!emailEnabled && (
              <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5 leading-relaxed">
                <DownloadSimple weight="bold" className="w-3 h-3 inline-block mr-1 text-slate-400" />
                Both <span className="font-medium">.docx</span> and <span className="font-medium">.pdf</span> will be saved to this case&apos;s Documents tab and downloaded to your device.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={generating} data-testid="generate-cancel-btn">Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating || !selectedTplId} className="text-white" style={{ background: "#100f5d" }} data-testid="generate-confirm-btn">
              {generating ? (emailEnabled ? "Generating & sending…" : "Generating…") : (emailEnabled ? "Generate & Email" : "Generate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
